import classNames from 'classnames'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Link, useNavigate} from 'react-router-dom'
import Badge from '../../components/core/badge'
import {ImgButton, SimpleBlueButton} from '../../components/core/button'
import Confirm from '../../components/core/confirm'
import CustomSelect from '../../components/core/customSelect'
import Dropdown from '../../components/core/dropdown'
import InputWithIcon from '../../components/core/inputWithIcon'
import ContentLoader from '../../components/core/loader'
import Table from '../../components/core/table'
import {PageTitle, ResponsivePageTitle} from '../../components/core/title'
import Tooltip from '../../components/core/tooltip'
import Footer from '../../components/layouts/footer'
import TeamSwitcher from '../../components/pages/teams'
import {
    DownIcon,
    FileNotFound,
    FilterIcon,
    HistoryIcon,
    PlusIcon,
    QuestionMarkIcon,
    SearchIcon,
    TrendingUpIcon,
    UpIcon
} from '../../icons'
import {routes} from '../../routes'
import {setRangeSelected} from '../../stores/monitoring'
import {getServerResources} from '../../stores/resources'
import {setServerResource} from '../../stores/server-monitoring'
import {colors} from '../../utils/colors'
import Customer from '../../utils/customer'
import NavigationHelper from '../../utils/navigation'
import {sortBySelected} from '../../utils/sorting'
import NotFound from '../resources/NotFound'
import EmptyContent from './EmptyContent'
import os from '../../data/selectOptions/os.json'
import ResourceState from '../../components/pages/resource/ResourceState'
import Modal from '../../components/core/modal'
import IncidentsModal from '../../components/pages/ResourceIncidents'
import {osIcons} from '../../utils/osIcons'
import useHeaderActions from './UseHeaderActions'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../utils/env'

const SORT_ID = 'server-monitoring-resources'
const customFieldLogic = text => ({
    enabled: (item) => item?.enabled ? text.enabled : text.disabled
})
const DASHBOARD_PATH = '/server-monitoring'
const {liveUpdateTime, isReadOnlyModeEnabled} = envConfig

const ServerMonitoring = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const text = useNamespaceTranslations('server-monitoring')
    const dropdownMenuRef = useRef()
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const serverResources = useSelector(state => state.resources.serverResources)
    const isFetching = useSelector(state => state.resources.isFetching)
    const isDeleting = useSelector(state => state.resources.isDeleting)
    const services = useSelector(state => state.resources.services)

    const serviceId = localStorage.getItem('serviceId')

    const [searchFilters, setSearchFilters] = useState({
        searchedText: '',
        selectedServices: []
    })
    const [filteredResources, setFilteredResources] = useState([])
    const [sortedResources, setSortedResources] = useState([])
    const [selectedResources, setSelectedResources] = useState([])
    const [isNotFound, setIsNotFound] = useState(false)

    const {
        handleAddServer,
        deleteResource,
        getActionsMenu,
        state,
        setFilteredMetrics,
        getActions,
        handleIncidentMenu} = useHeaderActions({isFetching, selectedResources})

    const renderResourceName = useCallback(resource => (
        <div className="resource-name-field min-width">
            <Link className="resource-name" to={NavigationHelper.getNavigationUrl(
                NavigationHelper.getLocalizedPath(routes.server_resource.paths),
                ':id',
                resource?.id)}>{resource?.name}</Link>
            {!resource?.setup_complete && <ResourceState tooltipText={text.warning_text}/>}
        </div>
    ), [])

    const getResourceStatus = (resource) => (
        <Badge className="lowercase" color={resource?.enabled ? text.success_status : text.warning_status}>
            {resource?.enabled ? text.enabled_status : text.disabled_status}
        </Badge>
    )

    const handleSearchFilters = (value, hasSearchedText = true) => {
        setSearchFilters({
            ...searchFilters,
            [hasSearchedText ? 'searchedText' : 'selectedServices']: value
        })
    }

    const getSearchedActions = () => (
        <div className="search-filter-actions">
            <div className={classNames('search-actions', {
                dark: isDarkMode
            })}>
                <InputWithIcon
                    placeholder={text.search}
                    icon={<SearchIcon />}
                    type="text"
                    value={searchFilters.searchedText}
                    onChange={(e) => handleSearchFilters(e.target.value)}
                />
                <Dropdown
                    closeMenu={true}
                    innerRef={dropdownMenuRef}
                    menuButton={<button className="filter-button"><FilterIcon />{text.filters}</button>}>
                    <CustomSelect
                        options={services}
                        name="service_id"
                        placeholder={text.select_service}
                        isMulti
                        isClearable={false}
                        value={searchFilters.selectedServices}
                        onChange={(_, data) => handleSearchFilters(data, false)}
                    />
                    <div className="d-flex w-100 justify-content-between gap-1">
                        <SimpleBlueButton className="outline w-100" onClick={() => searchResource(true)}>{text.reset}</SimpleBlueButton>
                        <SimpleBlueButton className="w-100" onClick={() => searchResource()}>{text.apply}</SimpleBlueButton>
                    </div>
                </Dropdown>
            </div>
            {Customer.isOwnTeam() && getActions({state: DASHBOARD_PATH})}
        </div>
    )

    const tableHeader = [
        {
            label: text.name,
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'name_asc',
                    field: ['name']
                },
                desc: {
                    key: 'name_desc',
                    field: ['name']
                }
            }
        },
        {
            label: text.timezone,
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'timezone_asc',
                    field: ['timezone']
                },
                desc: {
                    key: 'timezone_desc',
                    field: ['timezone']
                }
            }
        },
        {
            label: text.operating_system,
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'os_asc',
                    field: ['os']
                },
                desc: {
                    key: 'os_desc',
                    field: ['os']
                }
            }
        },
        {
            label: text.status,
            className: 'min-width-250',
            sorting: {
                asc: {
                    key: 'status_asc',
                    field: ['enabled']
                },
                desc: {
                    key: 'status_desc',
                    field: ['enabled']
                }
            }
        }
    ]

    const visitButton = (id) => {
        return (
            <>
                <Tooltip content={text.view_statistics}>
                    <Link
                        to={NavigationHelper.getNavigationUrl(
                            NavigationHelper.getLocalizedPath(routes.server_resource.paths), ':id', id)}
                        className={classNames({
                            'table-action-btn': true,
                            dark: isDarkMode
                        })}
                    >
                        <TrendingUpIcon/>
                    </Link>
                </Tooltip>
                <Tooltip content={text.incidents}>
                    <span
                        onClick={() => handleIncidentMenu(id)}
                        className={classNames({
                            'table-action-btn': true,
                            dark: isDarkMode
                        })}
                    >
                        <HistoryIcon fill={isDarkMode ? colors.colorFilterTitle : colors.colorMuted}/>
                    </span>
                </Tooltip>
            </>
        )
    }

    const generateTableBody = sortedResources?.map((serverResource) => {
        const selectedOsOption = os?.[serverResource?.os]
        const IconComponent = selectedOsOption ? osIcons[selectedOsOption.icon] : null

        return [
            renderResourceName(serverResource),
            <span className='min-width'>{serverResource?.timezone}</span>,
            <span className='min-width'>
                <Tooltip content={selectedOsOption ? selectedOsOption?.label : text.not_available} trigger='mouseenter' placement='bottom' noButton>
                    <span className={classNames('os-icon', {
                        'os-icon-not-available': !selectedOsOption
                    })}>{selectedOsOption ? <IconComponent height={selectedOsOption?.height || 28}/> : <QuestionMarkIcon fill={isDarkMode ? colors.colorFilterTitle : colors.colorMuted}/>}</span>
                </Tooltip>
                <span className='state-icon'>
                    <ResourceState
                        tooltipText={
                            !serverResource?.settings?.interval
                                ? text.disabled_interval_warning_text
                                : serverResource?.has_open_incidents
                                    ? text.down
                                    : text.up
                        }
                        icon={serverResource?.settings?.interval && (serverResource?.has_open_incidents ? <DownIcon /> : <UpIcon />)}
                        placement='bottom'
                    />
                </span>
            </span>,
            <span className="d-flex justify-content-between min-width-250">{getResourceStatus(serverResource)}
                <span>
                    {visitButton(serverResource?.id)}
                    {Customer.isOwnTeam() && getActionsMenu(serverResource, DASHBOARD_PATH)}
                </span>
            </span>
        ]
    })

    const getBody = () => (
        <>
            {isFetching ? (
                <ContentLoader />
            ) : (
                <>
                    <TeamSwitcher />
                    {!serverResources?.length ? (
                        <EmptyContent addServerResource={() => handleAddServer(navigate)} />
                    ) : (
                        <div className="server-monitoring-container">
                            {getSearchedActions()}
                            {isNotFound ? (
                                <NotFound message={text.resources_not_found} icon={<FileNotFound />} />
                            ) : (
                                <Table
                                    tableHeader={tableHeader}
                                    data={sortedResources}
                                    tbodyData={generateTableBody}
                                    identificator="id"
                                    isMultiCheckable
                                    selectedData={selectedResources}
                                    setSelectedData={setSelectedResources}
                                    hasCheckbox={Customer.isOwnTeam()}
                                    isSortingEnabled
                                    setData={setSortedResources}
                                    sortId={SORT_ID}
                                    filteredData={filteredResources}
                                    customFieldLogic={customFieldLogic(text)}
                                />
                            )}
                        </div>
                    )}
                </>
            )}
        </>
    )

    const searchResource = (reset = false) => {
        dropdownMenuRef?.current?.closeMenu()
        setIsNotFound(false)
        if (reset) {
            setSearchFilters({searchedText: '', selectedServices: services})
            setFilteredResources(serverResources)
            return
        }

        const modifiedResources = serverResources?.filter(resource =>
            searchFilters.selectedServices.some(service => service.service_id === resource.service_id)
        )
        if (!modifiedResources?.length) {
            setIsNotFound(true)
        }
        setFilteredResources(modifiedResources)
    }

    useEffect(() => {
        const modifiedResources = serverResources?.filter(resource =>
            resource.name.toLowerCase().includes(searchFilters.searchedText.toLowerCase()) &&
            (searchFilters.selectedServices.some(service => service.service_id === resource.service_id))
        )
        setFilteredResources(modifiedResources)
        setIsNotFound(modifiedResources?.length === 0)
    }, [searchFilters?.searchedText])

    useEffect(() => {
        if (serviceId) {
            dispatch(setRangeSelected(false))
            dispatch(getServerResources(true))
        }
        setIsNotFound(false)
    }, [serviceId])

    useEffect(() => {
        if (services?.length) {
            setSearchFilters({searchedText: '', selectedServices: services})
            setSelectedResources([])
            setSelectedResources([])
        }
    }, [services])

    useEffect(() => {
        if (serverResources?.length) {
            setFilteredResources(serverResources)
            setSortedResources(serverResources)
        }
        setFilteredMetrics([])
    }, [serverResources])

    useEffect(() => {
        dispatch(setServerResource({data: {}}))
    }, [])

    useEffect(() => {
        setSortedResources(sortBySelected(filteredResources, SORT_ID, tableHeader, customFieldLogic(text)))
    }, [filteredResources])

    useEffect(() => {
        if (state.activeIncidentsModalId) return
        const interval = setInterval(() => {
            dispatch(getServerResources())
        }, liveUpdateTime)

        return () => clearInterval(interval)
    }, [dispatch])

    const handleDelete = () => {
        deleteResource(state.deletedResourceId ? [state.deletedResourceId] : selectedResources)
        setSelectedResources([])
    }

    const activeServerResource = serverResources?.find(resource => resource?.id === state.activeIncidentsModalId)

    return (
        <main className={classNames('main', {
            dark: isDarkMode
        })} id="server-monitoring">
            {!!state.activeIncidentsModalId && <Modal
                isOpen={state.activeIncidentsModalId}
                contentClassName="overflow-auto"
                id="large-modal"
                title={text.incidents}
                closeModal={() => handleIncidentMenu(state.activeIncidentsModalId)}
            >
                <span className='incident-modal-sub-title'>
                    <Tooltip content={text.view_statistics}>
                        <Link
                            to={NavigationHelper.getNavigationUrl(
                                NavigationHelper.getLocalizedPath(routes.server_resource.paths),
                                ':id',
                                state.activeIncidentsModalId)}
                            className={classNames({
                                'table-action-btn': true,
                                dark: isDarkMode
                            })}
                        >
                            <TrendingUpIcon/>
                        </Link>
                    </Tooltip>
                    <p className='incident-modal-sub-title-text'>{activeServerResource?.name}</p>
                </span>
                <IncidentsModal
                    resourceId={state.activeIncidentsModalId}
                    resourceType='ServerResource'
                />
            </Modal>}
            <Confirm
                body={text.are_you_sure}
                callback={handleDelete}
                showLoader={isDeleting}
            />
            <div>
                <ResponsivePageTitle />
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <span className={classNames('section', {
                                dark: isDarkMode
                            })}>{text.section}</span>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                    {Customer.isOwnTeam() && (!!serverResources.length) && (
                        <ImgButton onClick={() => handleAddServer(navigate)} disabled={isReadOnlyModeEnabled}>
                            <PlusIcon stroke={colors.colorWhite} />
                            {text.add_server_monitoring}
                        </ImgButton>
                    )}
                </div>
                {getBody()}
            </div>
            <Footer />
        </main>
    )
}

export default ServerMonitoring
